import { useEffect, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { useRefresh, useUpdate } from "react-admin";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import axiosInstance from "@/utils/axiosInstance";
import { Settings } from "@/interface/entityInterface";
import { BaseStaticSelect } from "@/components/input/CustomInputs";
import { currencies } from "@/utils/variables";

const FieldRow = ({
  label,
  description,
  component,
}: {
  label: string;
  description?: string;
  component: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col sm:flex-row items-center py-5 border-t">
      <div className="w-full sm:w-1/3 pr-4">
        <Label className="block text-[18px] font-medium mb-1">{label}</Label>
        {description && (
          <p className="text-[14px] text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="w-full sm:w-1/4">{component}</div>
    </div>
  );
};

const AccountSettings = ({ settings }: { settings: Settings | null }) => {
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [formProp, setFormProp] = useState<{ currency: string }>({
    currency: "",
  });
  const [open, setOpen] = useState(false);
  const [error] = useState(false);
  const [loading, setLoading] = useState(false);

  const [update] = useUpdate();
  const refresh = useRefresh();

  const [name, setName] = useState("");
  const [image, setImage] = useState<any>(null);

  useEffect(() => {
    if (!settings) return;
    if (settings?.name) {
      setName(settings.name);
    }
    if (settings?.currency) setFormProp({ currency: settings.currency });
    if (settings?.company_logo) setImage(settings?.company_logo);
  }, [settings]);

  const handleSave = async () => {
    setLoading(true);
    if (settings?.name !== name) {
      const updatedData = {
        name,
      };

      update(
        "settings",
        { id: settings?.id, data: updatedData },
        {
          onSuccess: () => {
            toast("Settings updated successfully!");
          },
          onError: (error) => {
            toast("Failed to update settings.");
          },
        },
      );
    }
    if (settings?.company_logo !== image && image) {
      try {
        const formData = new FormData();
        formData.append("file", image);
        const { data } = await axiosInstance.post("/upload/image", formData);

        await axiosInstance.put("/settings/logo", {
          company_logo: data.result.variants[0],
        });
        toast("Image uploaded");
        refresh();
      } catch (err: any) {
        toast(err.response.data.message || "An error occured");
      }
    }
    if (settings?.currency !== formProp.currency) {
      const updatedData = {
        currency: formProp.currency,
      };
      
      update(
        "settings",
        { id: settings?.id, data: updatedData },
        {
          onSuccess: () => {
            toast("Settings updated successfully!");
          },
          onError: (error) => {
            toast("Failed to update settings.");
          },
        },
      );
    }
    setLoading(false);
  };

  if (!settings) return <p>Loading...</p>;
  if (error) return <p>Error loading settings.</p>;

  return (
    <div className="space-y-2 mt-6">
      <FieldRow
        label="Image"
        description="Your company image"
        component={
          <div className="flex items-center gap-x-5">
            <Avatar
              className="w-12 h-12 cursor-pointer"
              onClick={() => setOpen(true)}
            >
              {
                <AvatarImage
                  src={
                    image
                      ? typeof image !== "string"
                        ? URL.createObjectURL(image)
                        : image
                      : settings.company_logo || undefined
                  }
                />
              }
              <AvatarFallback>
                {settings?.name
                  ? settings?.name.split(" ")[0][0] +
                    settings?.name.split(" ")[1][0]
                  : ""}
              </AvatarFallback>
            </Avatar>
            <Button
              type="button"
              onClick={() => imageInputRef.current?.click()}
            >
              Change image
            </Button>
            <a
              className="font-medium cursor-pointer"
              onClick={() => setImage(null)}
            >
              Reset
            </a>
            <input
              className="hidden"
              onChange={(e) =>
                setImage(e.currentTarget?.files?.item(0) || null)
              }
              ref={imageInputRef}
              type="file"
              accept=".png, .jpg, .jpeg"
            />
          </div>
        }
      />
      <FieldRow
        label="Name"
        description="Your company name."
        component={
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        }
      />
      <FieldRow
        label="Currency"
        description="Choose the currency your company operates with"
        component={
          <BaseStaticSelect
            id="currency"
            value={formProp.currency}
            label=""
            data={currencies}
            setValue={setFormProp}
          />
        }
      />
      {/* <FieldRow
        label="Company Type"
        description="Share this ID with the customer support team to speed up your account lookup."
        component={
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {settings?.settings_type === "transportation_agency" &&
                "Transportation Agency"}
            </Badge>
          </div>
        }
      /> */}
      {settings?.api_key && (
        <FieldRow
          label="Plugin Key"
          description="Share this Api Key with your developer website to connect your website with your system."
          component={
            <div className="flex items-center gap-2">
              <Input readOnly defaultValue={settings?.api_key} />
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigator.clipboard.writeText(settings?.api_key)}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          }
        />
      )}
      <FieldRow
        label="ID"
        description="Share this ID with the customer support team to speed up your account lookup."
        component={
          <div className="flex items-center gap-2">
            <Input readOnly value={settings?.id} />
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigator.clipboard.writeText(settings?.id as any)}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] sm:max-w-[80vw] flex items-center justify-center bg-transparent border-0 shadow-none">
          <img
            src={
              image
                ? typeof image !== "string"
                  ? URL.createObjectURL(image)
                  : image
                : settings.company_logo || undefined
            }
            alt={"Company profile"}
            className="max-h-[80vh] max-w-[80vw] rounded-lg"
          />
        </DialogContent>
      </Dialog>
      <div className="flex pt-6">
        <Button disabled={loading} onClick={handleSave}>
          {loading ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default AccountSettings;
